import { useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, notifyErr, errMsg } from "@/lib/api";

export function AiVehicleFill({ onSingle, onList }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const read = async (list) => {
    const files = [...list];
    if (!files.length) { return; }
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    setBusy(true);
    try {
      const { data: { job_id } } = await api.post("/ai/vehicle-extract", fd);
      let j = { status: "running" };
      for (let i = 0; i < 120 && j.status === "running"; i += 1) {
        await new Promise((res) => { setTimeout(res, 2500); });
        j = (await api.get(`/ai/vehicle-extract/${job_id}`)).data;
      }
      if (j.status === "running") { toast.error("La lettura sta impiegando troppo tempo: riprova con meno file"); return; }
      if (j.status === "error") { toast.error(j.error); return; }
      const r = { data: j.result };
      r.data.warnings.forEach((w) => toast.warning(w));
      const vs = r.data.vehicles;
      if (vs.length === 1) {
        onSingle(vs[0].fields, vs[0].photo_index != null ? files[vs[0].photo_index] : null);
        toast.success(`Dati letti: ${Object.keys(vs[0].fields).length} campi compilati${vs[0].exists ? " (targa già presente in flotta)" : ""}`);
      } else if (vs.length > 1) { onList(vs, files); }
    } catch (e) { notifyErr(e); } finally { setBusy(false); if (ref.current) { ref.current.value = ""; } }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-800 p-3" data-testid="vehicle-ai-box">
      <input ref={ref} type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={(e) => read(e.target.files)} data-testid="vehicle-ai-files-input" />
      <Button type="button" variant="outline" disabled={busy} onClick={() => ref.current?.click()} data-testid="vehicle-ai-fill-btn">
        {busy ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Sparkles size={15} className="mr-1" />}{busy ? "Lettura in corso…" : "Compila con AI"}</Button>
      <span className="text-xs text-zinc-400">Carica foto dell'auto, libretto o la foto di un elenco di auto: marca, modello, targa e gli altri dati si compilano da soli.</span>
    </div>
  );
}

const lbl = (f) => [f.plate, f.brand, f.model].filter(Boolean).join(" · ") || "Veicolo senza targa";
const extra = (f) => [f.year, f.fuel, f.color, f.vin && `Telaio ${f.vin}`].filter(Boolean).join(" · ");

export function AiVehicleBulk({ data, onClose, onDone, aiPhotos }) {
  const [sel, setSel] = useState(() => new Set((data?.list || []).map((v, i) => (!v.exists && v.fields.plate && v.fields.brand && v.fields.model ? i : -1)).filter((i) => i >= 0)));
  const [busy, setBusy] = useState(false);
  const toggle = (i) => setSel((s) => { const n = new Set(s); if (n.has(i)) { n.delete(i); } else { n.add(i); } return n; });
  const create = async () => {
    setBusy(true);
    let ok = 0, needAi = false;
    for (const i of [...sel].sort((a, b) => a - b)) {
      const v = data.list[i];
      try {
        const r = await api.post("/vehicles", v.fields);
        ok += 1;
        const photo = v.photo_index != null ? data.files[v.photo_index] : null;
        if (photo && photo.type.startsWith("image/")) { const fd = new FormData(); fd.append("file", photo); await api.post(`/vehicles/${r.data.id}/photo`, fd).catch(() => {}); }
        else if (aiPhotos) { needAi = true; }
      } catch (e) { toast.error(`${lbl(v.fields)}: ${errMsg(e)}`); }
    }
    setBusy(false);
    if (ok) { toast.success(`${ok} veicoli creati`); }
    if (needAi) { api.post("/vehicles/ai-photos").then((r) => toast.info(r.data.message)).catch(() => {}); }
    onDone(); onClose();
  };
  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto scroll-thin" data-testid="vehicle-ai-bulk">
        <DialogHeader><DialogTitle>Veicoli trovati: {data?.list.length}</DialogTitle>
          <DialogDescription>Seleziona i veicoli da creare. Quelli già in flotta o senza targa, marca o modello non sono selezionati: potrai completarli dopo.</DialogDescription></DialogHeader>
        <div className="divide-y divide-zinc-800 border border-zinc-800 rounded-lg">
          {data?.list.map((v, i) => (
            <label key={i} className="flex items-center gap-3 p-3 cursor-pointer" data-testid={`vehicle-ai-row-${i}`}>
              <Checkbox checked={sel.has(i)} onCheckedChange={() => toggle(i)} data-testid={`vehicle-ai-check-${i}`} />
              <span className="min-w-0"><span className="block text-sm font-medium truncate">{lbl(v.fields)}{v.exists && <span className="ml-2 text-xs text-amber-300">già in flotta</span>}</span>
                <span className="block text-xs text-zinc-400 truncate">{extra(v.fields)}</span></span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="vehicle-ai-bulk-cancel">Annulla</Button>
          <Button className="btn-brand" disabled={busy || !sel.size} onClick={create} data-testid="vehicle-ai-bulk-create">{busy && <Loader2 size={14} className="mr-1 animate-spin" />}Crea {sel.size} veicoli</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
